<script lang="ts" setup>
import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { navigation } from '@/composition/navigation'
import { SearchState } from '@/composition/search'
import { MemberStatus } from '@/models/Groups'
import NavigationItem from '@/models/Navigation'
import { useAppStore } from '@/store/app'
import { getNumberAbbreviation } from '@/util/NumberUtils'
import { isGokollab } from '@ghl/clientportal-core/init'
import { UITextMdMedium } from '@gohighlevel/ghl-ui'
import { computed, defineAsyncComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import CommunityButton from '../../buttons/CommunityButton.vue'

const LoginModal = defineAsyncComponent(
  () => import('@/components/login/LoginModal.vue')
)
const InvitationLinkModal = defineAsyncComponent(
  () => import('@/components/groups/InvitationLinkModal.vue')
)
const GroupInvitations = defineAsyncComponent(
  () => import('@/components/groups/settings/GroupInvitations.vue')
)

const { t } = useI18n()
const appStore = useAppStore()
const router = useRouter()
const route = useRoute()
const tabSwitcher = ref<HTMLElement | null>(null)
const loginPage = ref('LOGIN')
const showLoginModal = ref(false)
const showInvitationModal = ref(false)

const handleToggle = (item: NavigationItem) => {
  if (GroupState.state.closedGroup) {
    return
  }
  if (
    ['group-home-search', 'channel-search'].includes(
      router.currentRoute.value.name as string
    )
  ) {
    return
  }
  router.push({ name: item.routeName[0] })
}

function getNavigationLink(item: NavigationItem) {
  if (GroupState.state.closedGroup) {
    return
  }
  if (
    ['group-home-search', 'channel-search'].includes(
      router.currentRoute.value.name as string
    )
  ) {
    return
  }

  let routePath = item.routeName[0]
  if (item.routeName[0] === 'group-member-list') {
    routePath = 'member-list'
  }
  if (item.routeName[0] === 'learning-home') {
    routePath = 'learning'
  }
  if (isGokollab()) {
    return `/${route.params.groupSlug}/${routePath}`
  }

  return `/communities/groups/${route.params.groupSlug}/${routePath}`
}

function openLogInModal() {
  showLoginModal.value = true
  loginPage.value = 'LOGIN'
}
// function openSignUpModal() {
//   showLoginModal.value = true
//   loginPage.value = 'SIGN_UP'
// }

const isSearchRoute = computed(() => {
  return [
    'group-home-search',
    'channel-search',
    'channel-search-post-view',
    'group-search-post-view',
  ].includes(router.currentRoute.value.name as string)
})
</script>
<template>
  <div
    ref="tabSwitcher"
    class="mx-6 flex w-full max-w-[1158px] items-center justify-between"
    :class="
      app.state.readingMode &&
      GroupState.state.userStatus === MemberStatus.ACTIVE &&
      'ml-16 xl:ml-6'
    "
  >
    <div class="flex cursor-pointer text-base text-communities-font-secondary">
      <div
        v-for="item in navigation.state.navigationItems?.filter(i => {
          if (isSearchRoute) {
            return i.showOnWeb && i.isSearchAvailable
          }
          return i.showOnWeb
        })"
        :id="`communities-${item.name.toLowerCase().replace('/', '-')}-btn`"
        :key="item.name"
        class="border-[3px] border-l-0 border-r-0 border-t-0 border-solid border-communities-background px-4 py-4 text-base font-medium hover:text-communities-font-primary"
        :active="item && item.routeName.includes(route.name as never)"
        :class="{
          'border-communities-font-primary  text-communities-font-primary': item && item.routeName.includes(route.name as never),
        }"
        tabindex="0"
        role="button"
        aria-label="Tab Switcher"
        @click="() => handleToggle(item as any)"
        @keyup.enter="() => handleToggle(item as any)"
      >
        <a
          :href="getNavigationLink(item as any)"
          class="text-inherit no-underline"
        >
          <div @click.stop.prevent="handleToggle(item as any)">
            <span> {{ t(item.label) }} </span>
          </div>
        </a>
        <span
          v-if="
            isSearchRoute &&
            (SearchState.state.searchDiscussionResults?.[0]?.total > 0 ||
              !SearchState.state.fetching)
          "
          class="ml-2"
        >
          {{
            getNumberAbbreviation(
              SearchState.state.searchDiscussionResults?.[0]?.total ?? 0
            )
          }}</span
        >
      </div>
    </div>
    <div>
      <div v-if="!appStore.isAuthenticated" class="flex gap-2">
        <CommunityButton
          id="login_button "
          class="font flex !h-10 w-28 gap-2"
          button-type="tertiary"
          @click="openLogInModal"
        >
          <UITextMdMedium>LOG IN</UITextMdMedium>
        </CommunityButton>
        <!-- <CommunityButton
          id="login_button "
          class="flex !h-10 w-28 gap-2"
          @click="openSignUpModal"
          buttonType="tertiary"
        >
          <UITextMdMedium>SIGN UP</UITextMdMedium>
        </CommunityButton> -->
      </div>
    </div>
  </div>
  <LoginModal
    v-if="showLoginModal"
    :page="loginPage"
    :location-data="app.state.locationData"
    :show="showLoginModal"
    :login-confirmation="() => {}"
    @update:show="showLoginModal = false"
  />
  <InvitationLinkModal
    v-if="showInvitationModal"
    :show="showInvitationModal"
    @update:show="showInvitationModal = false"
  />
  <GroupInvitations v-if="app.state.showInviteModal" />
</template>
<style scoped></style>
